import { useState } from 'react';
import {
  Fade,
  Paper,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid } from '@mui/x-data-grid';
import { useParams } from 'react-router-dom';
import { useNotification } from 'hook/useNotification';
import MDBox from 'components/MDBox';
import {
  useDeleteKnowledgeBaseDocumentsMutation,
  useGetKnowledgeBaseDocumentsQuery,
  useGetKnowledgeBaseQuery
} from 'services/knowledgeBaseApi';
import KnowledgeBaseCustomToolbar from 'pages/knowledgeBase/components/knowledgeBaseCustomToolbar';
import MDButton from 'components/MDButton';

/**
 * Component for displaying Knowledge Base Documents.
 *
 * @component
 *
 * @example
 * <KnowledgeBaseDocuments />
 *
 * @returns {React.Component} The Knowledge Base Documents component.
 */
export default function KnowledgeBaseDocuments() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const { knowledgeBaseId } = useParams();

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });

  const { data: knowledgeBase, isLoading: knowledgeBaseLoading } =
    useGetKnowledgeBaseQuery(knowledgeBaseId);

  const {
    data: knowledgeBaseDocumentsPage,
    error: knowledgeBaseDocumentsError,
    isLoading: knowledgeBaseDocumentsLoading
  } = useGetKnowledgeBaseDocumentsQuery({
    knowledgeBaseId,
    page: paginationModel.page,
    size: paginationModel.pageSize
  });

  const documents = knowledgeBaseDocumentsPage?.content || [];
  const totalElements = knowledgeBaseDocumentsPage?.totalElements || 0;

  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const selectedRows = rowSelectionModel.map((id) => documents.find((key) => key.id === id));

  // État pour contrôler l'ouverture/fermeture de la boîte de dialogue de confirmation
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [deleteDocuments] = useDeleteKnowledgeBaseDocumentsMutation();

  const columns = [
    {
      field: 'id',
      headerName: msg({ id: `page.documents.table.column.id` }),
      width: 500,
      disableColumnMenu: true
    },
    {
      field: 'metadata',
      headerName: msg({ id: `page.documents.table.column.filename` }),
      width: 1000,
      disableColumnMenu: true,
      renderCell: (params) => `${params.value?.filename}`
    }
  ];

  if (knowledgeBaseDocumentsError) {
    addNotification({
      type: 'error',
      title: msg({ id: 'notification.warning.title' }),
      message: msg({ id: 'page.documents.fetch.error' })
    });
  }

  const handleOpenConfirmDialog = () => {
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmedDelete = async () => {
    if (selectedRows) {
      deleteDocuments({
        id: knowledgeBaseId,
        documentIds: selectedRows.map((row) => row.id)
      })
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.knowledgeBase.documents.delete.success' })
          });
          clearSelection();
          handleCloseConfirmDialog();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.knowledgeBase.documents.delete.error' })
          });
          handleCloseConfirmDialog();
        });
    }
  };

  function clearSelection() {
    setRowSelectionModel([]);
  }

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox
        flex={1}
        display="flex"
        flexDirection="column"
        sx={{ height: '100%' }}
        id="knowledgeBaseDocumentsContainer"
      >
        <Paper elevation={2} sx={{ borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}>
          <MDBox sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h6" ml={2}>
              {knowledgeBaseLoading ? 'Chargement en cours' : knowledgeBase.label}
            </Typography>
          </MDBox>
        </Paper>
        <Paper
          flex={1}
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            rows={documents}
            columns={columns}
            rowCount={totalElements}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            pageSizeOptions={[10]}
            paginationMode="server"
            slots={{ toolbar: KnowledgeBaseCustomToolbar }}
            slotProps={{
              toolbar: {
                onDelete: handleOpenConfirmDialog,
                selectedRows
              }
            }}
            checkboxSelection
            loading={knowledgeBaseDocumentsLoading}
            onRowSelectionModelChange={setRowSelectionModel}
            rowSelectionModel={rowSelectionModel}
          />
          {/* Boîte de dialogue de confirmation */}
          <Dialog
            open={confirmDialogOpen}
            onClose={handleCloseConfirmDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">
              {msg({ id: 'page.knowledgeBase.documents.delete.confirm.title' })}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                {selectedRows && selectedRows.length > 1
                  ? msg(
                      { id: 'page.knowledgeBase.documents.delete.confirm.multiple' },
                      { count: selectedRows.length }
                    )
                  : msg({ id: 'page.knowledgeBase.documents.delete.confirm.single' })}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MDButton onClick={handleCloseConfirmDialog} color="secondary">
                {msg({ id: 'button.cancel' })}
              </MDButton>
              <MDButton onClick={handleConfirmedDelete} color="primary">
                {msg({ id: 'button.delete' })}
              </MDButton>
            </DialogActions>
          </Dialog>
        </Paper>
      </MDBox>
    </Fade>
  );
}
